import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { leaguesApi, leagueLeaderboardApi } from '../services/api';
import { exportCSV } from '../utils/export';
import './Leagues.css';

function Leagues({ user, onLogout }) {
  const navigate = useNavigate();
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingLeague, setEditingLeague] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website: '',
    contactEmail: '',
    contactPhone: '',
    logo: '',
    isActive: true,
    defaultBlindDuration: 20,
    defaultBreakDuration: 10,
    defaultBreakFrequency: 4,
    defaultBlindStructureId: '',
    timezone: 'America/Denver',
  });
  const [saving, setSaving] = useState(false);
  const [blindStructures, setBlindStructures] = useState([]);
  const [numberOfPlaces, setNumberOfPlaces] = useState(5);
  const [pointsConfig, setPointsConfig] = useState([]);
  const [pointsSaving, setPointsSaving] = useState(false);
  const [pointsMessage, setPointsMessage] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [leaderboardLeague, setLeaderboardLeague] = useState(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);

  const handleShowLeaderboard = async (league) => {
    setLeaderboardLeague(league);
    setShowLeaderboard(true);
    setLeaderboardLoading(true);
    try {
      const res = await leagueLeaderboardApi.get(league.id);
      setLeaderboardData(res.data?.leaderboard || res.leaderboard || []);
    } catch (e) {
      console.error('Error loading leaderboard:', e);
      setLeaderboardData([]);
    } finally {
      setLeaderboardLoading(false);
    }
  };

  useEffect(() => {
    fetchLeagues();
  }, []);

  const fetchLeagues = async () => {
    try {
      setLoading(true);
      const response = await leaguesApi.getAll();
      // Handle nested response structures
      const leaguesList = response.data?.leagues || response.leagues || response.data || [];
      setLeagues(Array.isArray(leaguesList) ? leaguesList : []);
    } catch (err) {
      console.error('Error fetching leagues:', err);
      setError('Failed to load leagues');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = async (league = null) => {
    if (league) {
      setEditingLeague(league);
      setFormData({
        name: league.name || '',
        description: league.description || '',
        website: league.website || '',
        contactEmail: league.contactEmail || league.contact_email || '',
        contactPhone: league.contactPhone || league.contact_phone || '',
        logo: league.logo || '',
        isActive: league.isActive !== undefined ? league.isActive : league.is_active !== undefined ? league.is_active : true,
        defaultBlindDuration: league.defaultBlindDuration || league.default_blind_duration_minutes || 20,
        defaultBreakDuration: league.defaultBreakDuration || league.default_break_duration_minutes || 10,
        defaultBreakFrequency: league.defaultBreakFrequency || league.default_break_frequency || 4,
        defaultBlindStructureId: league.defaultBlindStructureId || league.default_blind_structure_id || '',
        timezone: league.timezone || 'America/Denver',
      });

      // Fetch blind structures for this league
      try {
        const response = await leaguesApi.getBlindStructures(league.id);
        const structures = response.data?.blindStructures || response.blindStructures || response.data || [];
        setBlindStructures(Array.isArray(structures) ? structures : []);
      } catch (err) {
        console.error('Error fetching blind structures:', err);
        setBlindStructures([]);
      }

      // Fetch league points configuration
      try {
        const pointsResponse = await leaguesApi.getPoints(league.id);
        const existingPoints = pointsResponse.data?.leaguePoints || pointsResponse.leaguePoints || [];
        if (existingPoints.length > 0) {
          setNumberOfPlaces(existingPoints.length);
          setPointsConfig(existingPoints.map(p => ({ place: p.place, points: p.points })));
        } else {
          // Default: 5 places with suggested points
          const defaultPoints = [
            { place: 1, points: 100 },
            { place: 2, points: 60 },
            { place: 3, points: 40 },
            { place: 4, points: 20 },
            { place: 5, points: 10 },
          ];
          setNumberOfPlaces(5);
          setPointsConfig(defaultPoints);
        }
      } catch (err) {
        console.error('Error fetching league points:', err);
        const defaultPoints = [
          { place: 1, points: 100 },
          { place: 2, points: 60 },
          { place: 3, points: 40 },
          { place: 4, points: 20 },
          { place: 5, points: 10 },
        ];
        setNumberOfPlaces(5);
        setPointsConfig(defaultPoints);
      }
      setPointsMessage(null);
    } else {
      setEditingLeague(null);
      setFormData({
        name: '',
        description: '',
        website: '',
        contactEmail: '',
        contactPhone: '',
        logo: '',
        isActive: true,
        defaultBlindDuration: 20,
        defaultBreakDuration: 10,
        defaultBreakFrequency: 4,
        defaultBlindStructureId: '',
        timezone: 'America/Denver',
      });
      setBlindStructures([]);
      setNumberOfPlaces(5);
      setPointsConfig([
        { place: 1, points: 100 },
        { place: 2, points: 60 },
        { place: 3, points: 40 },
        { place: 4, points: 20 },
        { place: 5, points: 10 },
      ]);
      setPointsMessage(null);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingLeague(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const getOrdinal = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  const handleNumberOfPlacesChange = (newCount) => {
    const count = parseInt(newCount);
    if (count < 1 || count > 10) return;
    setNumberOfPlaces(count);

    // Adjust pointsConfig array
    const newConfig = [];
    for (let i = 1; i <= count; i++) {
      const existing = pointsConfig.find(p => p.place === i);
      newConfig.push({
        place: i,
        points: existing ? existing.points : 0,
      });
    }
    setPointsConfig(newConfig);
  };

  const handlePointsChange = (place, value) => {
    const pointsValue = parseInt(value) || 0;
    setPointsConfig(prev =>
      prev.map(p => p.place === place ? { ...p, points: pointsValue } : p)
    );
  };

  const handleSavePoints = async () => {
    if (!editingLeague) return;
    setPointsSaving(true);
    setPointsMessage(null);
    try {
      await leaguesApi.savePoints(editingLeague.id, pointsConfig);
      setPointsMessage({ type: 'success', text: 'Points configuration saved!' });
    } catch (err) {
      console.error('Error saving league points:', err);
      setPointsMessage({ type: 'error', text: 'Failed to save points configuration' });
    } finally {
      setPointsSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const leagueData = {
        name: formData.name,
        description: formData.description || null,
        website: formData.website || null,
        contactEmail: formData.contactEmail || null,
        contactPhone: formData.contactPhone || null,
        logo: formData.logo || null,
        isActive: formData.isActive,
        defaultBlindDuration: parseInt(formData.defaultBlindDuration),
        defaultBreakDuration: parseInt(formData.defaultBreakDuration),
        defaultBreakFrequency: parseInt(formData.defaultBreakFrequency),
        defaultBlindStructureId: formData.defaultBlindStructureId || null,
        timezone: formData.timezone,
      };

      if (editingLeague) {
        await leaguesApi.update(editingLeague.id, leagueData);
      } else {
        await leaguesApi.create(leagueData);
      }
      handleCloseModal();
      fetchLeagues();
    } catch (err) {
      console.error('Error saving league:', err);
      setError(editingLeague ? 'Failed to update league' : 'Failed to create league');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (leagueId) => {
    if (!window.confirm('Are you sure you want to delete this league? This will affect all associated venues and games.')) {
      return;
    }

    try {
      await leaguesApi.delete(leagueId);
      fetchLeagues();
    } catch (err) {
      console.error('Error deleting league:', err);
      setError('Failed to delete league');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="admin-layout">
      <Sidebar user={user} onLogout={onLogout} />

      <main className="main-content">
        <div className="page-header">
          <h2>Poker Leagues</h2>
          <p>Manage your poker leagues and organizations</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="card">
          <div className="card-header">
            <h3>All Leagues</h3>
            <button className="btn btn-primary" onClick={() => handleOpenModal()}>
              + Create League
            </button>
          </div>

          {loading ? (
            <div className="loading">
              <div className="loading-spinner"></div>
            </div>
          ) : leagues.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🏆</div>
              <h3>No leagues yet</h3>
              <p>Create your first poker league to get started.</p>
              <button className="btn btn-primary" onClick={() => handleOpenModal()}>
                Create League
              </button>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Contact</th>
                  <th>Timezone</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {leagues.map(league => (
                  <tr key={league.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {league.logo && (
                          <img
                            src={league.logo}
                            alt={league.name}
                            style={{ width: '32px', height: '32px', borderRadius: '4px', objectFit: 'cover' }}
                          />
                        )}
                        <strong>{league.name}</strong>
                      </div>
                    </td>
                    <td>
                      <div style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {league.description || '-'}
                      </div>
                    </td>
                    <td>
                      {league.contactEmail || league.contact_email ? (
                        <div style={{ fontSize: '14px' }}>
                          <div>{league.contactEmail || league.contact_email}</div>
                          {(league.contactPhone || league.contact_phone) && (
                            <div style={{ color: '#666' }}>{league.contactPhone || league.contact_phone}</div>
                          )}
                        </div>
                      ) : '-'}
                    </td>
                    <td style={{ fontSize: '13px' }}>{league.timezone || 'Not set'}</td>
                    <td>
                      <span className={`badge ${(league.isActive || league.is_active) ? 'badge-success' : 'badge-secondary'}`}>
                        {(league.isActive || league.is_active) ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{formatDate(league.createdAt || league.created_at)}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleShowLeaderboard(league)}
                        >
                          Leaderboard
                        </button>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => navigate(`/leagues/${league.id}/blind-structures`)}
                        >
                          Blinds
                        </button>
                        {league.website && (
                          <a
                            href={league.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-secondary btn-sm"
                          >
                            Visit
                          </a>
                        )}
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleOpenModal(league)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDelete(league.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Create/Edit League Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{editingLeague ? 'Edit League' : 'Create League'}</h3>
                <button className="modal-close" onClick={handleCloseModal}>&times;</button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label htmlFor="name">League Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="form-input"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., Ace High Poker League"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="description">Description</label>
                    <textarea
                      id="description"
                      name="description"
                      className="form-input"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Brief description of your league"
                      rows="3"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="website">Website</label>
                    <input
                      type="url"
                      id="website"
                      name="website"
                      className="form-input"
                      value={formData.website}
                      onChange={handleInputChange}
                      placeholder="https://example.com"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="contactEmail">Contact Email</label>
                    <input
                      type="email"
                      id="contactEmail"
                      name="contactEmail"
                      className="form-input"
                      value={formData.contactEmail}
                      onChange={handleInputChange}
                      placeholder="contact@example.com"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="contactPhone">Contact Phone</label>
                    <input
                      type="tel"
                      id="contactPhone"
                      name="contactPhone"
                      className="form-input"
                      value={formData.contactPhone}
                      onChange={handleInputChange}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="logo">Logo URL</label>
                    <input
                      type="url"
                      id="logo"
                      name="logo"
                      className="form-input"
                      value={formData.logo}
                      onChange={handleInputChange}
                      placeholder="https://example.com/logo.png"
                    />
                    {formData.logo && (
                      <div style={{ marginTop: '8px' }}>
                        <img
                          src={formData.logo}
                          alt="Logo preview"
                          style={{
                            width: '64px',
                            height: '64px',
                            borderRadius: '4px',
                            objectFit: 'cover',
                            border: '1px solid #ddd'
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="timezone">League Timezone</label>
                    <select
                      id="timezone"
                      name="timezone"
                      className="form-input"
                      value={formData.timezone}
                      onChange={handleInputChange}
                    >
                      <option value="America/New_York">Eastern (America/New_York)</option>
                      <option value="America/Chicago">Central (America/Chicago)</option>
                      <option value="America/Denver">Mountain (America/Denver)</option>
                      <option value="America/Los_Angeles">Pacific (America/Los_Angeles)</option>
                      <option value="America/Phoenix">Arizona (America/Phoenix)</option>
                      <option value="America/Anchorage">Alaska (America/Anchorage)</option>
                      <option value="Pacific/Honolulu">Hawaii (Pacific/Honolulu)</option>
                      <option value="UTC">UTC</option>
                    </select>
                    <p style={{ fontSize: '13px', color: '#666', margin: '4px 0 0 0' }}>
                      Game times and schedules will display in this timezone
                    </p>
                  </div>

                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        style={{ width: 'auto' }}
                      />
                      Active League
                    </label>
                    <p style={{ fontSize: '14px', color: '#666', margin: '4px 0 0 0' }}>
                      Inactive leagues won't be visible to players
                    </p>
                  </div>

                  <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />

                  <h4 style={{ marginBottom: '16px', color: '#333' }}>Blind Structure Settings</h4>

                  {editingLeague && (
                    <div className="form-group">
                      <label htmlFor="defaultBlindStructureId">Default Blind Structure</label>
                      <div className="league-blind-structure-row">
                        <select
                          id="defaultBlindStructureId"
                          name="defaultBlindStructureId"
                          className="form-input"
                          value={formData.defaultBlindStructureId}
                          onChange={handleInputChange}
                          style={{ flex: 1 }}
                        >
                          <option value="">None - Use league defaults</option>
                          {blindStructures.map(structure => (
                            <option key={structure.id} value={structure.id}>
                              {structure.name}
                              {structure.is_default ? ' (Default)' : ''}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => {
                            handleCloseModal();
                            navigate(`/leagues/${editingLeague.id}/blind-structures`);
                          }}
                          title="Manage blind structures"
                        >
                          Manage Blinds
                        </button>
                      </div>
                      <p style={{ fontSize: '13px', color: '#666', margin: '4px 0 0 0' }}>
                        Select a blind structure to use for new games, or manage your blind structures
                      </p>
                    </div>
                  )}

                  <h4 style={{ marginBottom: '16px', marginTop: '24px', color: '#333' }}>Default Blind Settings</h4>
                  <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
                    These settings will be used as defaults when no blind structure is selected
                  </p>

                  <div className="form-group">
                    <label htmlFor="defaultBlindDuration">Blind Level Duration (minutes)</label>
                    <input
                      type="number"
                      id="defaultBlindDuration"
                      name="defaultBlindDuration"
                      className="form-input"
                      value={formData.defaultBlindDuration}
                      onChange={handleInputChange}
                      min="1"
                      max="120"
                      required
                    />
                    <p style={{ fontSize: '13px', color: '#666', margin: '4px 0 0 0' }}>
                      How long each blind level lasts
                    </p>
                  </div>

                  <div className="form-group">
                    <label htmlFor="defaultBreakDuration">Break Duration (minutes)</label>
                    <input
                      type="number"
                      id="defaultBreakDuration"
                      name="defaultBreakDuration"
                      className="form-input"
                      value={formData.defaultBreakDuration}
                      onChange={handleInputChange}
                      min="1"
                      max="60"
                      required
                    />
                    <p style={{ fontSize: '13px', color: '#666', margin: '4px 0 0 0' }}>
                      How long breaks last
                    </p>
                  </div>

                  <div className="form-group">
                    <label htmlFor="defaultBreakFrequency">Break Frequency (levels)</label>
                    <input
                      type="number"
                      id="defaultBreakFrequency"
                      name="defaultBreakFrequency"
                      className="form-input"
                      value={formData.defaultBreakFrequency}
                      onChange={handleInputChange}
                      min="1"
                      max="20"
                      required
                    />
                    <p style={{ fontSize: '13px', color: '#666', margin: '4px 0 0 0' }}>
                      Insert a break every N blind levels
                    </p>
                  </div>

                  {editingLeague && (
                    <>
                      <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />

                      <h4 style={{ marginBottom: '16px', color: '#333' }}>Points Configuration</h4>
                      <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
                        Configure how many finishing places earn points and how many points each place receives
                      </p>

                      <div className="form-group">
                        <label htmlFor="numberOfPlaces">Number of Paid Places</label>
                        <select
                          id="numberOfPlaces"
                          className="form-input"
                          value={numberOfPlaces}
                          onChange={(e) => handleNumberOfPlacesChange(e.target.value)}
                          style={{ maxWidth: '200px' }}
                        >
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                            <option key={n} value={n}>{n} {n === 1 ? 'place' : 'places'}</option>
                          ))}
                        </select>
                        <p style={{ fontSize: '13px', color: '#666', margin: '4px 0 0 0' }}>
                          How many finishing positions earn league points
                        </p>
                      </div>

                      <div style={{
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        padding: '16px',
                        marginTop: '12px',
                      }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid #dee2e6', fontSize: '14px', color: '#495057' }}>
                                Place
                              </th>
                              <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid #dee2e6', fontSize: '14px', color: '#495057' }}>
                                Points
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {pointsConfig.map((entry) => (
                              <tr key={entry.place}>
                                <td style={{ padding: '6px 12px', borderBottom: '1px solid #e9ecef', fontSize: '14px', fontWeight: '500' }}>
                                  {entry.place === 1 ? '🥇' : entry.place === 2 ? '🥈' : entry.place === 3 ? '🥉' : '  '}{' '}
                                  {getOrdinal(entry.place)} Place
                                </td>
                                <td style={{ padding: '6px 12px', borderBottom: '1px solid #e9ecef' }}>
                                  <input
                                    type="number"
                                    className="form-input"
                                    value={entry.points}
                                    onChange={(e) => handlePointsChange(entry.place, e.target.value)}
                                    min="0"
                                    max="10000"
                                    style={{ width: '120px', padding: '6px 10px' }}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {pointsMessage && (
                        <div style={{
                          marginTop: '12px',
                          padding: '8px 12px',
                          borderRadius: '4px',
                          fontSize: '14px',
                          backgroundColor: pointsMessage.type === 'success' ? '#d4edda' : '#f8d7da',
                          color: pointsMessage.type === 'success' ? '#155724' : '#721c24',
                          border: `1px solid ${pointsMessage.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
                        }}>
                          {pointsMessage.text}
                        </div>
                      )}

                      <div style={{ marginTop: '12px' }}>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleSavePoints}
                          disabled={pointsSaving}
                          style={{ fontSize: '14px' }}
                        >
                          {pointsSaving ? 'Saving Points...' : 'Save Points Configuration'}
                        </button>
                      </div>
                    </>
                  )}
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving
                      ? (editingLeague ? 'Updating...' : 'Creating...')
                      : (editingLeague ? 'Update League' : 'Create League')
                    }
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Leaderboard Modal */}
        {showLeaderboard && (
          <div className="modal-overlay" onClick={() => setShowLeaderboard(false)}>
            <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
              <div className="modal-header">
                <h3>{leaderboardLeague?.name} Leaderboard</h3>
                <button className="modal-close" onClick={() => setShowLeaderboard(false)}>&times;</button>
              </div>
              <div className="modal-body" style={{ maxHeight: 500, overflowY: 'auto' }}>
                {leaderboardLoading ? (
                  <div className="loading"><div className="loading-spinner"></div></div>
                ) : leaderboardData.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: '#888' }}>
                    <div style={{ fontSize: '48px', marginBottom: '12px' }}>🏆</div>
                    <h3 style={{ margin: '0 0 8px' }}>No results yet</h3>
                    <p>Standings will appear here as games are completed.</p>
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Player</th>
                        <th>Points</th>
                        <th>Games</th>
                        <th>Best</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboardData.map(player => (
                        <tr key={player.playerId}>
                          <td><strong>{player.rank <= 3 ? ['🥇','🥈','🥉'][player.rank - 1] : player.rank}</strong></td>
                          <td><strong>{player.firstName} {player.lastName}</strong></td>
                          <td><strong>{player.totalPoints}</strong></td>
                          <td>{player.gamesPlayed}</td>
                          <td>{player.bestFinish === 1 ? '1st' : player.bestFinish === 2 ? '2nd' : player.bestFinish === 3 ? '3rd' : `${player.bestFinish}th`}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="modal-footer">
                {leaderboardData.length > 0 && (
                  <button className="btn btn-primary" onClick={() => exportCSV(
                    `${leaderboardLeague?.name || 'league'}-leaderboard.csv`,
                    ['Rank', 'First Name', 'Last Name', 'Points', 'Games Played', 'Best Finish'],
                    leaderboardData.map(p => [p.rank, p.firstName, p.lastName, p.totalPoints, p.gamesPlayed, p.bestFinish])
                  )}>Export CSV</button>
                )}
                <button className="btn btn-secondary" onClick={() => setShowLeaderboard(false)}>Close</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Leagues;
